/**
 * The scanner-lead audience.
 *
 * A different unit from the register: `ArScanLead` is keyed by DOMAIN, so the
 * counts here are sites, not companies. Two companies on one domain are one
 * candidate, and one company on three domains is three. The preview labels it,
 * and this form says it once more above the fields.
 */
import { Input, Switch } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { LEAD_SEVERITIES, LEAD_STATUSES } from "../../../../lib/scanLeads";
import type { ScanAudienceFilter } from "../../../../lib/marketing";
import { Chip, Field } from "../shared";

const VERSION_GAPS = ["current", "patch", "minor", "major", "unknown"] as const;

/** CMS ids the scanner reports often enough to be worth a one-click chip.
 *  The free-text field below stays, because the scanner can name anything. */
const COMMON_CMS = ["wordpress", "wix", "squarespace", "shopify", "joomla", "drupal", "webflow"];

/** Toggle one value in an optional string array, dropping the key entirely when
 *  the list empties - an empty array and "no filter" must not be different
 *  things to the server. */
function toggle<T extends string>(list: T[] | undefined, value: T): T[] | undefined {
  const next = (list ?? []).includes(value) ? (list ?? []).filter((v) => v !== value) : [...(list ?? []), value];
  return next.length ? next : undefined;
}

/** Comma-separated text to a clean list, and back. */
function parseList(s: string): string[] | undefined {
  const out = [...new Set(s.split(",").map((v) => v.trim().toLowerCase()).filter(Boolean))].slice(0, 20);
  return out.length ? out : undefined;
}

export function ScanFilterForm({
  filter,
  onChange,
  disabled,
}: {
  filter: ScanAudienceFilter;
  onChange: (p: Partial<ScanAudienceFilter>) => void;
  disabled?: boolean;
}) {
  const t = useT();

  return (
    <div className="space-y-4">
      <p className="rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        {t("marketing.audience.scanUnitNote")}
      </p>

      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        <Field label={t("marketing.audience.severity")}>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {LEAD_SEVERITIES.map((s) => (
              <Chip
                key={s}
                active={(filter.severity ?? []).includes(s)}
                onClick={() => onChange({ severity: toggle(filter.severity, s) })}
                disabled={disabled}
              >
                {t(`leads.severity.${s}`)}
              </Chip>
            ))}
          </div>
        </Field>

        <Field label={t("marketing.audience.leadStatus")} hint={t("marketing.audience.leadStatusHint")}>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {LEAD_STATUSES.map((s) => (
              <Chip
                key={s}
                active={(filter.status ?? []).includes(s)}
                onClick={() => onChange({ status: toggle(filter.status, s) })}
                disabled={disabled}
              >
                {t(`leads.status.${s}`)}
              </Chip>
            ))}
          </div>
        </Field>

        <Field label={t("marketing.audience.score")}>
          <div className="flex items-center gap-2">
            <Input
              value={filter.scoreMin ?? ""}
              onChange={(e) => onChange({ scoreMin: e.target.value === "" ? undefined : Number(e.target.value) })}
              placeholder={t("marketing.audience.min")}
              inputMode="numeric"
              disabled={disabled}
            />
            <span className="text-xs text-muted-foreground">-</span>
            <Input
              value={filter.scoreMax ?? ""}
              onChange={(e) => onChange({ scoreMax: e.target.value === "" ? undefined : Number(e.target.value) })}
              placeholder={t("marketing.audience.max")}
              inputMode="numeric"
              disabled={disabled}
            />
          </div>
        </Field>

        <Field label={t("marketing.audience.versionGap")} hint={t("marketing.audience.versionGapHint")}>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {VERSION_GAPS.map((g) => (
              <Chip
                key={g}
                active={(filter.versionGap ?? []).includes(g)}
                onClick={() => onChange({ versionGap: toggle(filter.versionGap, g) })}
                disabled={disabled}
              >
                {t(`marketing.versionGap.${g}`)}
              </Chip>
            ))}
          </div>
        </Field>

        <Field label={t("marketing.audience.cms")} hint={t("marketing.audience.cmsHint")}>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {COMMON_CMS.map((c) => (
                <Chip
                  key={c}
                  active={(filter.cms ?? []).includes(c)}
                  onClick={() => onChange({ cms: toggle(filter.cms, c) })}
                  disabled={disabled}
                >
                  {c}
                </Chip>
              ))}
            </div>
            <Input
              value={(filter.cms ?? []).join(", ")}
              onChange={(e) => onChange({ cms: parseList(e.target.value) })}
              placeholder={t("marketing.audience.cmsPlaceholder")}
              disabled={disabled}
            />
          </div>
        </Field>

        <Field label={t("marketing.audience.cmsNot")} hint={t("marketing.audience.cmsNotHint")}>
          <Input
            value={(filter.cmsNot ?? []).join(", ")}
            onChange={(e) => onChange({ cmsNot: parseList(e.target.value) })}
            placeholder={t("marketing.audience.cmsPlaceholder")}
            disabled={disabled}
          />
        </Field>
      </div>

      <label className="flex items-center gap-2.5 text-sm">
        <Switch
          checked={filter.hasEmail ?? false}
          onCheckedChange={(v) => onChange({ hasEmail: v || undefined })}
          disabled={disabled}
        />
        <span>{t("marketing.audience.hasEmail")}</span>
      </label>
    </div>
  );
}
